import React, { useState } from 'react';
import {
  Download, Folder, FileText, Video, Headphones, Image,
  Archive, Trash2, Search, Filter, Calendar, Clock,
  Check, X, RefreshCw, Pause, Play, AlertCircle,
  ExternalLink, Share2, Eye, Star, File, Code,
  Database, BookOpen, Layers, CheckCircle, XCircle,
  RotateCcw, HardDrive, Cloud, Wifi, WifiOff
} from 'lucide-react';

interface DownloadItem {
  id: string;
  name: string;
  type: 'video' | 'audio' | 'document' | 'image' | 'archive' | 'code' | 'dataset' | 'ebook';
  size: string;
  downloadedAt: string;
  status: 'completed' | 'downloading' | 'paused' | 'failed' | 'queued';
  progress: number;
  url?: string;
  thumbnail?: string;
  course?: string;
  category: string;
  author?: string;
  duration?: number;
  isOffline: boolean;
  expiresAt?: string;
  downloadSpeed?: string;
  timeRemaining?: string;
}

interface DownloadQueue {
  id: string;
  name: string;
  type: string;
  size: string;
  status: 'queued' | 'downloading' | 'paused' | 'failed';
  progress: number;
  downloadSpeed?: string;
  timeRemaining?: string;
}

export default function Downloads() {
  const [activeTab, setActiveTab] = useState<'completed' | 'downloading' | 'failed'>('completed');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'date' | 'name' | 'size' | 'type'>('date');
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [isOfflineMode, setIsOfflineMode] = useState(false);

  const downloads: DownloadItem[] = [
    {
      id: '1',
      name: 'Complete React.js Masterclass - Chapter 1 Introduction',
      type: 'video',
      size: '245 MB',
      downloadedAt: '2024-01-30T14:30:00',
      status: 'completed',
      progress: 100,
      thumbnail: '/api/placeholder/400/225',
      course: 'React.js Masterclass',
      category: 'Programming',
      author: 'Sarah Chen',
      duration: 1800, // 30 minutes
      isOffline: true
    },
    {
      id: '2',
      name: 'Machine Learning Algorithms Cheat Sheet.pdf',
      type: 'document',
      size: '15.2 MB',
      downloadedAt: '2024-01-29T10:15:00',
      status: 'completed',
      progress: 100,
      course: 'Data Science Fundamentals',
      category: 'Data Science',
      author: 'Dr. Alex Kumar',
      isOffline: true,
      expiresAt: '2024-02-28T23:59:59'
    },
    {
      id: '3',
      name: 'Docker for Developers - Episode 5.mp3',
      type: 'audio',
      size: '67.3 MB',
      downloadedAt: '2024-01-29T16:45:00',
      status: 'completed',
      progress: 100,
      course: 'DevOps Podcast Series',
      category: 'DevOps',
      author: 'DevOps Weekly',
      duration: 2400, // 40 minutes
      isOffline: true
    },
    {
      id: '4',
      name: 'Node.js Advanced Patterns - Source Code.zip',
      type: 'archive',
      size: '89.1 MB',
      downloadedAt: '2024-01-28T12:20:00',
      status: 'completed',
      progress: 100,
      course: 'Advanced Node.js',
      category: 'Programming',
      author: 'Michael Park',
      isOffline: true
    },
    {
      id: '5',
      name: 'Customer Analytics Dataset.csv',
      type: 'dataset',
      size: '127 MB',
      downloadedAt: '2024-01-27T09:30:00',
      status: 'completed',
      progress: 100,
      category: 'Data Science',
      author: 'DataLab Team',
      isOffline: true
    },
    {
      id: '6',
      name: 'Design System Components Guide.pdf',
      type: 'ebook',
      size: '34.7 MB',
      downloadedAt: '2024-01-26T15:10:00',
      status: 'completed',
      progress: 100,
      course: 'UI/UX Design Masterclass',
      category: 'Design',
      author: 'Emma Rodriguez',
      isOffline: false,
      expiresAt: '2024-02-26T23:59:59'
    }
  ];

  const downloadQueue: DownloadQueue[] = [
    {
      id: 'q1',
      name: 'Python Data Structures - Lecture 3.mp4',
      type: 'video',
      size: '456 MB',
      status: 'downloading',
      progress: 67,
      downloadSpeed: '2.3 MB/s',
      timeRemaining: '2m 15s'
    },
    {
      id: 'q2',
      name: 'JavaScript ES6 Features Guide.pdf',
      type: 'document',
      size: '23.4 MB',
      status: 'queued',
      progress: 0
    },
    {
      id: 'q3',
      name: 'Web Security Checklist Template.docx',
      type: 'document',
      size: '8.7 MB',
      status: 'paused',
      progress: 34
    }
  ];

  const categories = [
    { id: 'all', name: 'All Categories', count: downloads.length },
    { id: 'Programming', name: 'Programming', count: downloads.filter(d => d.category === 'Programming').length },
    { id: 'Data Science', name: 'Data Science', count: downloads.filter(d => d.category === 'Data Science').length },
    { id: 'Design', name: 'Design', count: downloads.filter(d => d.category === 'Design').length },
    { id: 'DevOps', name: 'DevOps', count: downloads.filter(d => d.category === 'DevOps').length }
  ];

  const getFileIcon = (type: string) => {
    switch (type) {
      case 'video': return <Video className="w-5 h-5" />;
      case 'audio': return <Headphones className="w-5 h-5" />;
      case 'document': return <FileText className="w-5 h-5" />;
      case 'image': return <Image className="w-5 h-5" />;
      case 'archive': return <Archive className="w-5 h-5" />;
      case 'code': return <Code className="w-5 h-5" />;
      case 'dataset': return <Database className="w-5 h-5" />;
      case 'ebook': return <BookOpen className="w-5 h-5" />;
      default: return <File className="w-5 h-5" />;
    }
  };

  const getFileTypeColor = (type: string) => {
    switch (type) {
      case 'video': return 'text-red-600 bg-red-50';
      case 'audio': return 'text-purple-600 bg-purple-50';
      case 'document': return 'text-blue-600 bg-blue-50';
      case 'image': return 'text-green-600 bg-green-50';
      case 'archive': return 'text-orange-600 bg-orange-50';
      case 'code': return 'text-gray-600 bg-gray-50';
      case 'dataset': return 'text-indigo-600 bg-indigo-50';
      case 'ebook': return 'text-yellow-600 bg-yellow-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'downloading': return <Download className="w-4 h-4 text-blue-600 animate-pulse" />;
      case 'paused': return <Pause className="w-4 h-4 text-yellow-600" />;
      case 'failed': return <XCircle className="w-4 h-4 text-red-600" />;
      case 'queued': return <Clock className="w-4 h-4 text-gray-600" />;
      default: return <File className="w-4 h-4 text-gray-600" />;
    }
  };

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  const filteredDownloads = downloads.filter(download => {
    const matchesCategory = selectedCategory === 'all' || download.category === selectedCategory;
    const matchesSearch = download.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (download.course && download.course.toLowerCase().includes(searchTerm.toLowerCase())) ||
                         (download.author && download.author.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesTab = activeTab === 'completed' ? download.status === 'completed' :
                      activeTab === 'downloading' ? ['downloading', 'paused', 'queued'].includes(download.status) :
                      download.status === 'failed';
    return matchesCategory && matchesSearch && matchesTab;
  }).sort((a, b) => {
    switch (sortBy) {
      case 'date': return new Date(b.downloadedAt).getTime() - new Date(a.downloadedAt).getTime();
      case 'name': return a.name.localeCompare(b.name);
      case 'size': return parseFloat(b.size) - parseFloat(a.size);
      case 'type': return a.type.localeCompare(b.type);
      default: return 0;
    }
  });

  const handleSelectItem = (id: string) => {
    setSelectedItems(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedItems.length === filteredDownloads.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(filteredDownloads.map(d => d.id));
    }
  };

  const totalSize = downloads.reduce((acc, download) => {
    const size = parseFloat(download.size.split(' ')[0]);
    const unit = download.size.split(' ')[1];
    const sizeInMB = unit === 'GB' ? size * 1024 : size;
    return acc + sizeInMB;
  }, 0);

  const offlineItems = downloads.filter(d => d.isOffline).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-brand-green-600 to-brand-teal-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold mb-2 flex items-center gap-2">
              <Download className="w-7 h-7" />
              Downloads & Offline Content
            </h1>
            <p className="text-green-100">
              Manage your downloaded files and offline learning content
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setIsOfflineMode(!isOfflineMode)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                isOfflineMode
                  ? 'bg-white text-green-600'
                  : 'bg-white/20 backdrop-blur-sm hover:bg-white/30'
              }`}
            >
              {isOfflineMode ? <WifiOff className="w-4 h-4" /> : <Wifi className="w-4 h-4" />}
              {isOfflineMode ? 'Offline Mode' : 'Online Mode'}
            </button>
          </div>
        </div>

        {/* Download Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 text-center">
            <HardDrive className="w-6 h-6 mx-auto mb-2 text-green-200" />
            <p className="text-2xl font-bold">{(totalSize / 1024).toFixed(1)} GB</p>
            <p className="text-xs text-green-100">Total Size</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 text-center">
            <CheckCircle className="w-6 h-6 mx-auto mb-2 text-green-200" />
            <p className="text-2xl font-bold">{downloads.filter(d => d.status === 'completed').length}</p>
            <p className="text-xs text-green-100">Completed</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 text-center">
            <Cloud className="w-6 h-6 mx-auto mb-2 text-green-200" />
            <p className="text-2xl font-bold">{offlineItems}</p>
            <p className="text-xs text-green-100">Available Offline</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 text-center">
            <Download className="w-6 h-6 mx-auto mb-2 text-green-200" />
            <p className="text-2xl font-bold">{downloadQueue.length}</p>
            <p className="text-xs text-green-100">In Queue</p>
          </div>
        </div>
      </div>

      {/* Active Downloads */}
      {downloadQueue.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Active Downloads</h2>
          <div className="space-y-4">
            {downloadQueue.map((item) => (
              <div key={item.id} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                <div className={`p-2 rounded-lg ${getFileTypeColor(item.type)}`}>
                  {getFileIcon(item.type)}
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium text-gray-900">{item.name}</h3>
                    <div className="flex items-center gap-2">
                      {getStatusIcon(item.status)}
                      <span className="text-sm text-gray-600">{item.status}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-600 mb-2">
                    <span>{item.size}</span>
                    {item.downloadSpeed && <span>{item.downloadSpeed}</span>}
                    {item.timeRemaining && <span>{item.timeRemaining} remaining</span>}
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all ${
                        item.status === 'downloading' ? 'bg-brand-blue-600' :
                        item.status === 'paused' ? 'bg-yellow-600' :
                        'bg-gray-400'
                      }`}
                      style={{ width: `${item.progress}%` }}
                    ></div>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>{item.progress}% complete</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  {item.status === 'downloading' && (
                    <button className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg">
                      <Pause className="w-4 h-4" />
                    </button>
                  )}
                  {item.status === 'paused' && (
                    <button className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg">
                      <Play className="w-4 h-4" />
                    </button>
                  )}
                  <button className="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Main Downloads Interface */}
      <div className="bg-white rounded-lg border border-gray-200">
        {/* Tabs */}
        <div className="flex border-b border-gray-200">
          {[
            { id: 'completed', label: 'Completed', count: downloads.filter(d => d.status === 'completed').length },
            { id: 'downloading', label: 'In Progress', count: downloadQueue.length },
            { id: 'failed', label: 'Failed', count: downloads.filter(d => d.status === 'failed').length }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center gap-2 px-6 py-4 font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-brand-green-500 text-brand-green-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <span>{tab.label}</span>
              {tab.count > 0 && (
                <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="p-6">
          {/* Filters and Search */}
          <div className="flex flex-col lg:flex-row gap-4 items-center justify-between mb-6">
            <div className="flex flex-col md:flex-row gap-4 flex-1">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search downloads..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-green-500"
                />
              </div>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-green-500"
              >
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name} ({category.count})
                  </option>
                ))}
              </select>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
                className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-green-500"
              >
                <option value="date">Sort by Date</option>
                <option value="name">Sort by Name</option>
                <option value="size">Sort by Size</option>
                <option value="type">Sort by Type</option>
              </select>
            </div>
            
            {selectedItems.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">{selectedItems.length} selected</span>
                <button className="flex items-center gap-2 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm">
                  <Trash2 className="w-4 h-4" />
                  Delete
                </button>
              </div>
            )}
          </div>

          {/* Downloads List */}
          <div className="space-y-3">
            <div className="flex items-center gap-4 p-4 border-b border-gray-100">
              <input
                type="checkbox"
                checked={selectedItems.length === filteredDownloads.length && filteredDownloads.length > 0}
                onChange={handleSelectAll}
                className="w-4 h-4 text-brand-green-600 border-gray-300 rounded focus:ring-brand-green-500"
              />
              <div className="flex-1 font-medium text-gray-700">File Name</div>
              <div className="w-24 text-center font-medium text-gray-700">Type</div>
              <div className="w-20 text-center font-medium text-gray-700">Size</div>
              <div className="w-32 text-center font-medium text-gray-700">Downloaded</div>
              <div className="w-20 text-center font-medium text-gray-700">Status</div>
              <div className="w-32 text-center font-medium text-gray-700">Actions</div>
            </div>

            {filteredDownloads.map((download) => (
              <div key={download.id} className="flex items-center gap-4 p-4 hover:bg-gray-50 rounded-lg transition-colors">
                <input
                  type="checkbox"
                  checked={selectedItems.includes(download.id)}
                  onChange={() => handleSelectItem(download.id)}
                  className="w-4 h-4 text-brand-green-600 border-gray-300 rounded focus:ring-brand-green-500"
                />
                
                <div className="flex-1 flex items-center gap-3">
                  {download.thumbnail ? (
                    <img src={download.thumbnail} alt="" className="w-12 h-8 object-cover rounded" />
                  ) : (
                    <div className={`p-2 rounded-lg ${getFileTypeColor(download.type)}`}>
                      {getFileIcon(download.type)}
                    </div>
                  )}
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-900 line-clamp-1">{download.name}</h3>
                    {download.course && (
                      <p className="text-sm text-gray-600">{download.course}</p>
                    )}
                    {download.author && (
                      <p className="text-xs text-gray-500">by {download.author}</p>
                    )}
                  </div>
                </div>

                <div className="w-24 text-center">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${getFileTypeColor(download.type)}`}>
                    {download.type}
                  </span>
                </div>

                <div className="w-20 text-center">
                  <span className="text-sm text-gray-600">{download.size}</span>
                </div>

                <div className="w-32 text-center">
                  <span className="text-sm text-gray-600">
                    {new Date(download.downloadedAt).toLocaleDateString()}
                  </span>
                </div>

                <div className="w-20 text-center">
                  <div className="flex items-center justify-center gap-1">
                    {getStatusIcon(download.status)}
                    {download.isOffline && <Cloud className="w-3 h-3 text-blue-600" />}
                  </div>
                </div>

                <div className="w-32 text-center">
                  <div className="flex items-center justify-center gap-1">
                    <button className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded">
                      <Eye className="w-4 h-4" />
                    </button>
                    <button className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded">
                      <Share2 className="w-4 h-4" />
                    </button>
                    <button className="p-1.5 text-red-600 hover:text-red-900 hover:bg-red-50 rounded">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredDownloads.length === 0 && (
            <div className="text-center py-12">
              <Download className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No downloads found</h3>
              <p className="text-gray-600">
                {activeTab === 'completed' ? 'You haven\'t downloaded any content yet.' :
                 activeTab === 'downloading' ? 'No downloads in progress.' :
                 'No failed downloads.'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Storage Info */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Storage Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <HardDrive className="w-8 h-8 text-gray-600 mx-auto mb-2" />
            <p className="text-sm text-gray-600">Total Downloaded</p>
            <p className="text-2xl font-bold text-gray-900">{(totalSize / 1024).toFixed(1)} GB</p>
          </div>
          <div className="text-center">
            <Cloud className="w-8 h-8 text-blue-600 mx-auto mb-2" />
            <p className="text-sm text-gray-600">Available Offline</p>
            <p className="text-2xl font-bold text-gray-900">{offlineItems} files</p>
          </div>
          <div className="text-center">
            <AlertCircle className="w-8 h-8 text-orange-600 mx-auto mb-2" />
            <p className="text-sm text-gray-600">Expiring Soon</p>
            <p className="text-2xl font-bold text-gray-900">
              {downloads.filter(d => d.expiresAt && new Date(d.expiresAt) < new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)).length}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}